import PropTypes from 'prop-types'

import { connect } from 'react-redux'

import styled from '@emotion/styled'

import {
  selectCampaignRetailerLogoSrc,
  selectCampaignRetailerName,
  selectCampaignName,
  selectCampaignRetailerPageURL,
  selectCampaignRetailerId,
} from '../../../../flux/selectors/campaign'

import {
  selectBrandColorBg,
} from '../../../../flux/selectors/layout'

import {
  uxEvent,
} from '../../../../flux/actions/ux'

import isWebShareAvailable from '../../../../utils/isWebShareAvailable'
import Icon from '../../../ui/Icon'
import { ReactComponent as ShareIcon } from '../../../../images/svg/share.svg'
import { selectIsWebView } from '../../../../flux/selectors/ux'

import { selectRetailerLogo } from '../../../../flux/selectors/retailers'
import LoadingSkeleton from '../../Generics/LoadingSkeleton'

import RetailerLink from './RetailerLink'

const StyledRoot = styled.div(({ theme: { spaces, colors } }) => ({
  height: (spaces.default / 2) * 8, // 64px
  borderBottom: `1px solid ${colors.light_grey}`,
}))

const StyledContainer = styled.div(({ theme }) => ({
  padding: `0 ${theme.spaces.default}px`,
  maxWidth: theme.breakpoints.xlarge,
  margin: '0 auto',
  width: 'auto',
  display: 'flex',
  alignItems: 'center',
  height: '100%',
}))

const StyledContent = styled.div(({ theme }) => ({
  flex: '1 1 0',
  display: 'flex',
  flexDirection: 'column',
  padding: `0px ${theme.spaces.default / 2}px`,
  minWidth: '0px',
}))

const StyledTitle = styled.span(({ theme: { fontSize } }) => ({
  flex: '1 1 auto',
  fontSize: fontSize * 1.0625,
  fontWeight: '800',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}))

const StyledSubTitle = styled.span(({ theme: { fontSize, colors } }) => ({
  flex: '1 1 auto',
  fontSize: fontSize * 0.75,
  fontWeight: '700',
  lineHeight: `${fontSize * 1.125}px`,
  color: colors.mid_grey,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}))

const StyledShare = styled.div(() => ({
  flex: '0 0 auto',
}))

const StyledShareIcon = styled.button(({ theme }) => ({
  color: theme.colors.primary,
  background: theme.colors.white,
  width: '42px',
  border: 'none',
}))

const CampaignHeaderLogoSkelton = () => (<LoadingSkeleton width="48px" height="48px" radius="50%" />)
const CampaignHeaderContentSkelton = () => (
  <>
    <LoadingSkeleton height="20px" width="100px" />
    <LoadingSkeleton height="20px" width="70px" />
  </>
)

const CampaignHeader = ({
  retailerLogoSrc,
  retailerLogoHref,
  retailerName,
  campaignName,
  brandColorBg,
  onLogoClick,
  onShareClick,
  isWebView,
}) => (!isWebView ? (
  <StyledRoot>
    <StyledContainer data-test="CampaignHeader">
      {
          retailerLogoSrc ? (
            <RetailerLink
              retailerLogoSrc={retailerLogoSrc}
              retailerLogoHref={retailerLogoHref}
              brandColorBg={brandColorBg}
              onLogoClick={onLogoClick}
              isRetailerLogoClickDisabled={!retailerLogoHref}
            />
          ) : <CampaignHeaderLogoSkelton />
      }

      <StyledContent>
        { retailerName && campaignName ? (
          <>
            <StyledTitle data-test="CampaignHeaderRetailer">
              {retailerName}
            </StyledTitle>
            <StyledSubTitle data-test="CampaignHeaderName">
              {campaignName}
            </StyledSubTitle>
          </>
        ) : <CampaignHeaderContentSkelton />}
      </StyledContent>
      {isWebShareAvailable() ? (
        <StyledShare onClick={onShareClick} data-test="CampaignHeaderShare">
          <StyledShareIcon>
            <Icon>
              <ShareIcon />
            </Icon>
          </StyledShareIcon>
        </StyledShare>
      ) : null}
    </StyledContainer>
  </StyledRoot>
) : null)

CampaignHeader.propTypes = {
  retailerLogoSrc: PropTypes.string,
  retailerLogoHref: PropTypes.string,
  retailerName: PropTypes.string,
  campaignName: PropTypes.string,
  isWebView: PropTypes.bool,
  brandColorBg: PropTypes.string,
  onLogoClick: PropTypes.func,
  onShareClick: PropTypes.func,
}

CampaignHeader.defaultProps = {
  isWebView: true,
  retailerLogoSrc: undefined,
  retailerLogoHref: '',
  retailerName: undefined,
  campaignName: undefined,
  brandColorBg: '',
  onLogoClick: '',
  onShareClick: () => false,
}

const mapStateToProps = state => {
  const retailerId = selectCampaignRetailerId(state)
  const customRetailerLogo = selectCampaignRetailerLogoSrc(state)
  const retailerLogo = selectRetailerLogo(retailerId)(state)

  return {
    retailerLogoSrc: customRetailerLogo || retailerLogo,
    retailerLogoHref: selectCampaignRetailerPageURL(state),
    retailerName: selectCampaignRetailerName(state),
    campaignName: selectCampaignName(state),
    brandColorBg: selectBrandColorBg(state),
    isWebView: selectIsWebView(state),
  }
}

const mapDispatchToProps = {
  uxEvent,
}

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...stateProps,
  ...dispatchProps,
  ...ownProps,
  onLogoClick: () => {
    dispatchProps.uxEvent({
      name: 'click',
      target: 'CampaignHeader/Logo',
      params: {},
    })
  },
  onShareClick: () => {
    dispatchProps.uxEvent({
      name: 'click',
      target: 'CampaignHeader/Share',
      params: {},
    })

    navigator.share({
      url: window.location.href,
      title: document.title,
    })
      .then(() => {
        dispatchProps.uxEvent({
          name: 'share',
          target: 'CampaignHeader/Share',
          params: {},
        })
      })
      .catch(console.warn) // eslint-disable-line no-console
  },
})

CampaignHeader.displayName = 'CampaignHeader'
CampaignHeader.whyDidYouRender = true

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(CampaignHeader)
